import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material'
import dayjs from 'dayjs'
import html2pdf from 'html2pdf.js'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import AppContext from '../../context/appContext'
import { useUploadObjectStoreFiles } from '../../hooks/data/useObjectStoreFiles'
import { useCustomSnackbarError } from '../../utils/Snackbar/useCustomSnackbarError'
import { NavHeader } from '../NavHeader/NavHeader'
import { AttestationReport } from './Attestation/AttestationReport'
import { REPORT_BACKGROUND_COLOR, SPACES_FOLDER } from './constants'
import { sanitizeString } from '../../utils/sanitizeStrings'

// ReportsHome sends these as ISO strings; the report body and the PDF filename
// both need real Dayjs instances.
const DATE_FIELDS = ['date', 'periodStart', 'periodEnd']

export const Report = () => {
  const { setHideNavbar } = useContext(AppContext)
  const { reportType } = useParams()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { handleError } = useCustomSnackbarError()
  const contentRef = useRef(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isReportLoading, setIsReportLoading] = useState(false)

  const report = useMemo(() => {
    if (!state?.report) return null
    const revived = { ...state.report }
    DATE_FIELDS.forEach(field => {
      if (revived[field]) revived[field] = dayjs(revived[field])
    })
    return revived
  }, [state])

  const uploadDir = report
    ? `farms/${report.farm.id}/reports/${SPACES_FOLDER[reportType]}`
    : undefined
  const { mutateAsync: uploadReport } = useUploadObjectStoreFiles(uploadDir)

  const renderReport = useMemo(
    () => ({
      Attestation: <AttestationReport report={report} setIsReportLoading={setIsReportLoading} />,
    }),
    [report],
  )

  useEffect(() => {
    if (!report) {
      navigate('/reports')
    }
  }, [report, navigate])

  useEffect(() => {
    setHideNavbar(true)
    return () => {
      setHideNavbar(false)
    }
  }, [setHideNavbar])

  const handleNavBack = () => {
    navigate('/reports')
  }

  const generatePdf = async () => {
    const element = contentRef.current
    const style = document.createElement('style')
    try {
      setIsUploading(true)
      const filename = `${reportType}_${sanitizeString(report.farm.name)}_${report.date.format('DD-MM-YYYY')}.pdf`
      const options = {
        filename,
        image: { type: 'jpeg', quality: 1 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }

      document.head.appendChild(style)
      style.sheet?.insertRule('body > div:last-child img { display: inline-block; }')
      element.style.backgroundColor = REPORT_BACKGROUND_COLOR[reportType] || 'white'
      element.style.minHeight = '100%'

      const pdf = await html2pdf().set(options).from(element).outputPdf('blob')

      const pdfFile = new File([pdf], filename, {
        type: 'application/pdf',
      })
      await uploadReport([pdfFile])

      await html2pdf().set(options).from(element).save()
      navigate('/reports')
    } catch (error) {
      handleError(error)
    } finally {
      style.remove()
      element.style.backgroundColor = ''
      element.style.minHeight = ''
      setIsUploading(false)
    }
  }

  return (
    <>
      <NavHeader>
        <NavHeader.BackButton handleNavBack={handleNavBack}>
          <ArrowBackIosIcon />
        </NavHeader.BackButton>
        <NavHeader.Title>
          <Typography variant="bodyBold">{reportType}</Typography>
        </NavHeader.Title>
        <NavHeader.FarmDetails />
      </NavHeader>
      {report ? (
        <>
          <Box
            sx={{
              display: 'flex',
              width: '210mm',
              margin: '24px auto',
              p: '16px',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <Button disabled={isUploading || isReportLoading} onClick={generatePdf}>
              Generar PDF
            </Button>
            {(isUploading || isReportLoading) && <CircularProgress size={24} />}
          </Box>
          <Paper
            sx={{
              width: '210mm',
              margin: '24px auto',
              backgroundColor: 'white',
              boxShadow: '0 0 0.5cm rgba(0,0,0,0.5)',
            }}
          >
            <div ref={contentRef}>{renderReport[reportType]}</div>
          </Paper>
        </>
      ) : null}
    </>
  )
}
